import { useCustomer } from "@/src/contexts/customer-context";
import { useToastStore } from "@/store/index";
import { useMutation } from "@tanstack/react-query";
import { addProductReview } from "../actions/products";
import { useState, useMemo } from "react";
import { debounce } from "lodash";

const ProductRating = ({
  rating = 0,
  product_id,
  readOnly = false,
  onRate,
}: {
  rating: number;
  product_id: string;
  readOnly?: boolean;
  onRate?: (rating: number) => void;
}) => {
  const { customer } = useCustomer();
  const { showToast } = useToastStore();
  const [hoveredRating, setHoveredRating] = useState<number | null>(null);

  const { mutate: reviewProduct } = useMutation({
    mutationFn: (rating: number) => addProductReview({ rating, product_id }),
    onSuccess: (_, variables) => {
      showToast("¡Gracias por tu calificación!", "ok");
      if (onRate) onRate(variables);
    },
    onError: () => {
      showToast("Error al enviar la calificación", "err");
    },
  });

  const debouncedReviewProduct = useMemo(
    () =>
      debounce((newRating: number) => reviewProduct(newRating), 500, {
        leading: false,
        trailing: true,
      }),
    [reviewProduct]
  );

  const handleRate = (newRating: number) => {
    if (readOnly) return;

    if (!customer) {
      showToast("Inicie sesión para calificar este producto", "warn");
      return;
    }
    // Optimistic update logic could go here if we managed local state fully
    debouncedReviewProduct(newRating);
    if (onRate) onRate(newRating); // Immediate feedback to parent if needed
  };

  return (
    <div
      className={`flex items-center gap-2 ${readOnly ? "" : "cursor-pointer"}`}
      onMouseLeave={() => setHoveredRating(null)}
    >
      <div className="hidden text-yellow-400 sm:flex">
        {[...Array(5)].map((_, i) => {
          const starValue = i + 1;
          const isFilled =
            (hoveredRating !== null ? hoveredRating : rating) >= starValue;

          return (
            <div
              key={i}
              className={readOnly ? "" : "transition-transform hover:scale-110"}
              onMouseEnter={() => !readOnly && setHoveredRating(starValue)}
              onClick={(e) => {
                e.stopPropagation();
                handleRate(starValue);
              }}
            >
              <span>{isFilled ? "★" : "☆"}</span>
            </div>
          );
        })}
      </div>
      <div className="text-yellow-400 sm:hidden">
        <span>{rating ? "★" : "☆"}</span>
      </div>
      {rating > 0 && (
        <span className="font-inter text-sm font-medium">
          {rating.toFixed(1)}
        </span>
      )}
    </div>
  );
};

export default ProductRating;
